import { useRef, useCallback } from 'react';

interface EventKey {
  key: string;
}

interface TrackedEvent {
  type: string;
  key: string;
  timestamp: number;
}

interface UseEventDeduplicationOptions {
  timeWindowMs: number;
  maxTrackedEvents: number;
}

interface UseEventDeduplicationReturn {
  isDuplicate: (type: string, event: EventKey) => boolean;
  trackEvent: (type: string, event: EventKey) => void;
  clear: () => void;
}

export function useEventDeduplication({
  timeWindowMs,
  maxTrackedEvents,
}: UseEventDeduplicationOptions): UseEventDeduplicationReturn {
  const trackedEventsRef = useRef<TrackedEvent[]>([]);

  const cleanupOldEvents = useCallback(() => {
    const now = Date.now();
    const events = trackedEventsRef.current;
    trackedEventsRef.current = events.filter(
      (event) => now - event.timestamp < timeWindowMs
    );

    // Limit to max events
    if (trackedEventsRef.current.length > maxTrackedEvents) {
      trackedEventsRef.current = trackedEventsRef.current.slice(-maxTrackedEvents);
    }
  }, [timeWindowMs, maxTrackedEvents]);

  const isDuplicate = useCallback(
    (type: string, event: EventKey): boolean => {
      cleanupOldEvents();
      const events = trackedEventsRef.current;
      return events.some(
        (tracked) => tracked.type === type && tracked.key === event.key
      );
    },
    [cleanupOldEvents]
  );

  const trackEvent = useCallback(
    (type: string, event: EventKey): void => {
      cleanupOldEvents();
      trackedEventsRef.current.push({
        type,
        key: event.key,
        timestamp: Date.now(),
      });
    },
    [cleanupOldEvents]
  );

  const clear = useCallback((): void => {
    trackedEventsRef.current = [];
  }, []);

  return { isDuplicate, trackEvent, clear };
}
